import { isInternationalShipment } from '@parallel-fluidics/shared/shipping';
import { useNotifications } from 'features/notifications';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import UnverifiedAddressModal from './unverifiedAddressModal';

const ShippingContext = createContext();
const DefaultParcel = Object.freeze({
  length: 6,
  width: 8,
  height: 6,
  weight: 16,
});

export default function ShippingProvider({ children }) {
  const { addNotification, removeNotification } = useNotifications();

  const [unverifiedData, setUnverifiedData] = useState({});
  const [toAddress, setToAddress] = useState({
    name: '',
    company: '',
    phone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  });
  const [parcelValues, setParcelValues] = useState([DefaultParcel]);
  const [parcelValidity, setParcelValidity] = useState(parcelValues.map(() => false));
  const [selectedShipFor, setSelectedShipFor] = useState(null);

  const [shipOrderId, setShipOrderId] = useState('');
  const [rates, setRates] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedRates, setSelectedRates] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const isInternational = useMemo(() => isInternationalShipment(toAddress), [toAddress]);

  // for international shipments
  const [customsInfo, setCustomsInfo] = useState(null);
  const [customsItems, setCustomsItems] = useState(null);

  // third party payment
  const [shippingPayment, setShippingPayment] = useState({
    type: 'SENDER',
  });

  const isParcelValid = useMemo(
    () => parcelValidity.every((valid) => valid),
    [parcelValidity],
  );

  const handleUpdateParcelNumbers = useCallback((newParcelNumbers) => {
    setParcelValues((prev) => {
      const newParcelValues = Array.from({ length: newParcelNumbers }, (_, index) => {
        if (prev[index]) return prev[index];
        return { ...DefaultParcel };
      });
      return newParcelValues;
    });
  }, []);

  const handleVerifyAddress = useCallback(async ({ onSuccess }) => {
    try {
      // onSuccess is a required parameter
      if (typeof onSuccess !== 'function') {
        throw new Error('onSuccess must be a function');
      }
      setIsLoading(true);
      removeNotification('handleVerifyAddress-error');
      if (!toAddress.street1) {
        throw new Error('Please enter a valid address');
      }
      const { data: verifyData } = await httpsCallable(getFunctions(), 'verifyShippingAddressV2')(toAddress);
      const { verifications } = verifyData;
      if (verifications?.delivery?.success === false) {
        // Show unverified address modal
        setUnverifiedData({
          errorMessage: verifications.delivery.errors?.[0]?.message || 'Invalid address',
          onConfirm: onSuccess,
        });
        return;
      }
      onSuccess();
    } catch (error) {
      addNotification({
        type: 'error',
        dismissible: true,
        content: `Error verifying address: ${error.message}`,
        id: 'handleVerifyAddress-error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, removeNotification, toAddress]);

  const handleGetRates = useCallback(async ({ onSuccess, packagesInfo }) => {
    if (!isParcelValid) return;
    try {
      setIsLoading(true);
      setRates([]);
      setShipOrderId('');
      setShipments([]);
      removeNotification('handleGetRates-error');

      const getShippingOrder = httpsCallable(getFunctions(), 'getShippingOrderV2');
      const { data } = await getShippingOrder({
        shipTo: toAddress,
        parcels: parcelValues,
        customsInfo,
        customsItems,
        shippingPayment,
        packagesInfo,
      });
      setShipOrderId(data.orderId);
      setRates(data.rates);
      setShipments(data.shipments);
      if (typeof onSuccess === 'function') onSuccess();
    } catch (error) {
      addNotification({
        type: 'error',
        dismissible: true,
        content: `Error getting rates: ${error.message}`,
        id: 'handleGetRates-error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    addNotification,
    isParcelValid,
    parcelValues,
    removeNotification,
    toAddress,
    customsInfo,
    customsItems,
    shippingPayment,
  ]);

  const handleClearRates = useCallback(() => {
    setRates([]);
    setShipOrderId('');
    setShipments([]);
    setSelectedRates([]);
  }, []);

  const handlePurchaseLabel = useCallback(async ({ onSuccess, onError, orderId = null }) => {
    try {
      setIsLoading(true);
      removeNotification('handlePurchaseLabel-error');
      if (selectedRates.length === 0) {
        throw new Error('Please select a rate');
      }
      const purchaseShippingLabel = httpsCallable(getFunctions(), 'purchaseShippingLabelV2');

      const { data } = await purchaseShippingLabel({
        shipOrderId,
        carrier: selectedRates[0].carrier,
        service: selectedRates[0].service,
        shipFor: selectedShipFor?.value,
        orderId,
      });
      setShipments(data.shipments);
      if (typeof onSuccess === 'function') onSuccess(data);
    } catch (error) {
      addNotification({
        type: 'error',
        dismissible: true,
        content: `Error purchasing label: ${error.message}`,
        id: 'handlePurchaseLabel-error',
      });
      if (typeof onError === 'function') onError(error);
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, removeNotification, selectedRates, selectedShipFor?.value, shipOrderId]);

  const value = useMemo(() => ({
    toAddress,
    isInternational,
    setToAddress,
    parcelValues,
    setParcelValues,
    isParcelValid,
    setParcelValidity,
    selectedShipFor,
    setSelectedShipFor,
    shipOrderId,
    rates,
    shipments,
    selectedRates,
    setSelectedRates,

    handleUpdateParcelNumbers,
    handleVerifyAddress,
    handleGetRates,
    handleClearRates,
    handlePurchaseLabel,

    isLoading,
    setIsLoading,

    customsInfo,
    setCustomsInfo,
    customsItems,
    setCustomsItems,

    shippingPayment,
    setShippingPayment,
  }), [
    toAddress,
    isInternational,
    parcelValues,
    isParcelValid,
    selectedShipFor,
    shipOrderId,
    rates,
    shipments,
    selectedRates,
    handleUpdateParcelNumbers,
    handleVerifyAddress,
    handleGetRates,
    handleClearRates,
    handlePurchaseLabel,
    isLoading,
    customsInfo,
    customsItems,
    shippingPayment,
  ]);

  return (
    <ShippingContext.Provider value={value}>
      {children}
      {!!unverifiedData.errorMessage && (
        <UnverifiedAddressModal
          visible
          onClose={() => setUnverifiedData({})}
          address={toAddress}
          onConfirm={unverifiedData.onConfirm}
          errorMessage={unverifiedData.errorMessage}
        />
      )}
    </ShippingContext.Provider>
  );
}
ShippingProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const useShipping = () => {
  const context = useContext(ShippingContext);
  if (context === undefined) {
    throw new Error('useShipping must be used within a ShippingProvider');
  }
  return context;
};
